/**
 * Invoice module CASL permissions (frontend catalog).
 *
 * Grant these in Admin Roles (backend abilities issued on login as
 * `userData.abilities`). There is no separate roles checkbox UI in this
 * client — sidebar/route gates use the same action + subject pairs.
 *
 * Subject `invoice` actions:
 *   read     — Finance → Invoice Tracker menu, dashboard, recon
 *   create   — Create (manual + upload), drafts, templates, formulae
 *   update   — Edit invoice, GSTR, payments, status, signed upload
 *   download — Export / PDF / JSON / Excel
 *   approve  — Approve & Issue (Pending Approval)
 *
 * Dashboard tabs each have their own subject. Do not reuse `invoice` (or leave)
 * to show every tab. Backend must seed these and issue them on login.
 * Run `migrations/seed_invoice_dashboard_permissions.sql`
 * then have users re-login so Admin Roles assignments take effect.
 *
 * Super-admin: `{ action: 'manage', subject: 'all' }` or roleId 1 sees every tab.
 */

export const INVOICE_PERMISSIONS = {
  SUBJECT: 'invoice',
}

/** CASL actions used with subject `invoice`. */
export const INVOICE_ACTIONS = {
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DOWNLOAD: 'download',
  APPROVE: 'approve',
}

/** Dedicated Invoice Tracker tab subjects (one per tab). */
export const INVOICE_TAB_PERMISSIONS = {
  OVERVIEW: 'invoice_overview',
  CANDIDATE_REVENUE: 'invoice_candidate_revenue',
  COLLECTIONS: 'invoice_collections',
  GSTR1: 'invoice_gstr1',
  GSTR3B: 'invoice_gstr3b',
  FINANCE_DASHBOARD: 'invoice_finance_dashboard',
  RECRUITER_DASHBOARD: 'invoice_recruiter_dashboard',
  CONTRACTUAL_BILLING: 'invoice_contractual_billing',
}

/** Stable tab keys used by InvoiceDashboard (not b-tabs visible indexes). */
export const INVOICE_DASHBOARD_TAB_KEYS = {
  OVERVIEW: 'overview',
  REVENUE: 'revenue',
  COLLECTIONS: 'collections',
  GSTR1: 'gstr1',
  GSTR3B: 'gstr3b',
  FINANCE: 'finance',
  RECRUITER: 'recruiter',
  CONTRACTUAL: 'contractual',
}

/** Preferred landing tab when the user is allowed to see it. */
export const DEFAULT_INVOICE_DASHBOARD_TAB = INVOICE_DASHBOARD_TAB_KEYS.OVERVIEW

export const INVOICE_DASHBOARD_TABS = [
  {
    key: INVOICE_DASHBOARD_TAB_KEYS.OVERVIEW,
    subject: INVOICE_TAB_PERMISSIONS.OVERVIEW,
    title: 'Invoice Overview',
    aliases: ['overview', 'invoice_overview'],
  },
  {
    key: INVOICE_DASHBOARD_TAB_KEYS.REVENUE,
    subject: INVOICE_TAB_PERMISSIONS.CANDIDATE_REVENUE,
    title: 'Candidate Revenue',
    aliases: ['revenue', 'candidate_revenue', 'invoice_candidate_revenue'],
  },
  {
    key: INVOICE_DASHBOARD_TAB_KEYS.COLLECTIONS,
    subject: INVOICE_TAB_PERMISSIONS.COLLECTIONS,
    title: 'Collections & Follow-ups',
    aliases: ['collections', 'invoice_collections'],
  },
  {
    key: INVOICE_DASHBOARD_TAB_KEYS.GSTR1,
    subject: INVOICE_TAB_PERMISSIONS.GSTR1,
    title: 'GSTR-1 Tracking',
    aliases: ['gstr1', 'invoice_gstr1'],
  },
  {
    key: INVOICE_DASHBOARD_TAB_KEYS.GSTR3B,
    subject: INVOICE_TAB_PERMISSIONS.GSTR3B,
    title: 'GSTR-3B Tracking',
    aliases: ['gstr3b', 'invoice_gstr3b'],
  },
  {
    key: INVOICE_DASHBOARD_TAB_KEYS.FINANCE,
    subject: INVOICE_TAB_PERMISSIONS.FINANCE_DASHBOARD,
    title: 'Finance Dashboard',
    aliases: ['finance', 'finance_dashboard', 'invoice_finance_dashboard'],
  },
  {
    key: INVOICE_DASHBOARD_TAB_KEYS.RECRUITER,
    subject: INVOICE_TAB_PERMISSIONS.RECRUITER_DASHBOARD,
    title: 'Recruiter Dashboard',
    aliases: ['recruiter', 'recruiter_dashboard', 'invoice_recruiter_dashboard'],
  },
  {
    key: INVOICE_DASHBOARD_TAB_KEYS.CONTRACTUAL,
    subject: INVOICE_TAB_PERMISSIONS.CONTRACTUAL_BILLING,
    title: 'Contractual Billing',
    aliases: ['contractual', 'contractual_billing', 'invoice_contractual_billing'],
  },
]

export const INVOICE_DOWNLOAD_DENIED_TOOLTIP = "You don't have permission to download invoices"

/**
 * Grantable CASL rows for Admin Roles. Copy action + subject as issued.
 * Suggested roles follow how the UI is used (Finance operates invoices;
 * Recruiter sees revenue/recruiter tabs; Admin gets everything).
 */
export const INVOICE_ABILITY_CATALOG = [
  {
    action: INVOICE_ACTIONS.READ,
    subject: INVOICE_PERMISSIONS.SUBJECT,
    name: 'Read invoices',
    unlocks: 'Finance → Invoice Tracker menu, Invoice Dashboard, Reconciliation',
    roles: ['Finance', 'Recruiter', 'Admin'],
  },
  {
    action: INVOICE_ACTIONS.CREATE,
    subject: INVOICE_PERMISSIONS.SUBJECT,
    name: 'Create invoices',
    unlocks: 'Create button, manual wizard, upload create, drafts, templates, formulae, Duplicate for next month',
    roles: ['Finance', 'Admin'],
  },
  {
    action: INVOICE_ACTIONS.UPDATE,
    subject: INVOICE_PERMISSIONS.SUBJECT,
    name: 'Update invoices',
    unlocks: 'Edit Invoice, Update GSTR, Update Status, Record Payment, Mark Disputed, Upload Signed',
    roles: ['Finance', 'Admin'],
  },
  {
    action: INVOICE_ACTIONS.DOWNLOAD,
    subject: INVOICE_PERMISSIONS.SUBJECT,
    name: 'Download invoices',
    unlocks: 'Toolbar export, PDF, JSON, e-Invoice Excel, supporting-doc download',
    roles: ['Finance', 'Admin'],
  },
  {
    action: INVOICE_ACTIONS.APPROVE,
    subject: INVOICE_PERMISSIONS.SUBJECT,
    name: 'Approve invoices',
    unlocks: 'Update Invoice → Approve & Issue / Return to Maker (Pending Approval)',
    roles: ['Finance', 'Admin'],
  },
  {
    action: INVOICE_ACTIONS.READ,
    subject: INVOICE_TAB_PERMISSIONS.OVERVIEW,
    name: 'Invoice Overview tab',
    unlocks: 'Invoice Tracker → Invoice Overview',
    roles: ['Finance', 'Admin'],
  },
  {
    action: INVOICE_ACTIONS.READ,
    subject: INVOICE_TAB_PERMISSIONS.CANDIDATE_REVENUE,
    name: 'Candidate Revenue tab',
    unlocks: 'Invoice Tracker → Candidate Revenue (edit revenue also needs update candidates / contractual_candidates)',
    roles: ['Finance', 'Recruiter', 'Admin'],
  },
  {
    action: INVOICE_ACTIONS.READ,
    subject: INVOICE_TAB_PERMISSIONS.COLLECTIONS,
    name: 'Collections tab',
    unlocks: 'Invoice Tracker → Collections & Follow-ups',
    roles: ['Finance', 'Admin'],
  },
  {
    action: INVOICE_ACTIONS.READ,
    subject: INVOICE_TAB_PERMISSIONS.GSTR1,
    name: 'GSTR-1 tab',
    unlocks: 'Invoice Tracker → GSTR-1 Tracking',
    roles: ['Finance', 'Admin'],
  },
  {
    action: INVOICE_ACTIONS.READ,
    subject: INVOICE_TAB_PERMISSIONS.GSTR3B,
    name: 'GSTR-3B tab',
    unlocks: 'Invoice Tracker → GSTR-3B Tracking',
    roles: ['Finance', 'Admin'],
  },
  {
    action: INVOICE_ACTIONS.READ,
    subject: INVOICE_TAB_PERMISSIONS.FINANCE_DASHBOARD,
    name: 'Finance Dashboard tab',
    unlocks: 'Invoice Tracker → Finance Dashboard',
    roles: ['Finance', 'Admin'],
  },
  {
    action: INVOICE_ACTIONS.READ,
    subject: INVOICE_TAB_PERMISSIONS.RECRUITER_DASHBOARD,
    name: 'Recruiter Dashboard tab',
    unlocks: 'Invoice Tracker → Recruiter Dashboard',
    roles: ['Recruiter', 'Admin'],
  },
  {
    action: INVOICE_ACTIONS.READ,
    subject: INVOICE_TAB_PERMISSIONS.CONTRACTUAL_BILLING,
    name: 'Contractual Billing tab',
    unlocks: 'Invoice Tracker → Contractual Billing',
    roles: ['Finance', 'Admin'],
  },
]

/** Suggested abilities by role for Admin Roles / backend login payload. */
export const INVOICE_ROLE_ABILITIES_MAP = {
  recruiter: [
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_PERMISSIONS.SUBJECT },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.RECRUITER_DASHBOARD },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.CANDIDATE_REVENUE },
  ],
  finance: [
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_PERMISSIONS.SUBJECT },
    { action: INVOICE_ACTIONS.CREATE, subject: INVOICE_PERMISSIONS.SUBJECT },
    { action: INVOICE_ACTIONS.UPDATE, subject: INVOICE_PERMISSIONS.SUBJECT },
    { action: INVOICE_ACTIONS.DOWNLOAD, subject: INVOICE_PERMISSIONS.SUBJECT },
    { action: INVOICE_ACTIONS.APPROVE, subject: INVOICE_PERMISSIONS.SUBJECT },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.OVERVIEW },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.CANDIDATE_REVENUE },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.COLLECTIONS },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.GSTR1 },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.GSTR3B },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.FINANCE_DASHBOARD },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.CONTRACTUAL_BILLING },
  ],
  admin: [
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_PERMISSIONS.SUBJECT },
    { action: INVOICE_ACTIONS.CREATE, subject: INVOICE_PERMISSIONS.SUBJECT },
    { action: INVOICE_ACTIONS.UPDATE, subject: INVOICE_PERMISSIONS.SUBJECT },
    { action: INVOICE_ACTIONS.DOWNLOAD, subject: INVOICE_PERMISSIONS.SUBJECT },
    { action: INVOICE_ACTIONS.APPROVE, subject: INVOICE_PERMISSIONS.SUBJECT },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.OVERVIEW },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.CANDIDATE_REVENUE },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.COLLECTIONS },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.GSTR1 },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.GSTR3B },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.FINANCE_DASHBOARD },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.RECRUITER_DASHBOARD },
    { action: INVOICE_ACTIONS.READ, subject: INVOICE_TAB_PERMISSIONS.CONTRACTUAL_BILLING },
  ],
}

function tryParseUserData() {
  try {
    return JSON.parse(localStorage.getItem('userData')) || {}
  } catch (e) {
    return {}
  }
}

function hasPermission(canFn, action, subject = INVOICE_PERMISSIONS.SUBJECT) {
  if (typeof canFn !== 'function') return false
  return !!canFn(action, subject)
}

/** Super-admin wildcard or roleId 1 (same as other modules). */
export function isInvoiceDashboardAdmin(canFn, user) {
  if (hasPermission(canFn, 'manage', 'all')) return true
  const data = user || tryParseUserData()
  const roleId = data.roleId ?? data.roleID ?? data.role_id
  return Number(roleId) === 1
}

/** Export / PDF / JSON download from invoice dashboard and related views. */
export function canDownloadInvoices(canFn) {
  return hasPermission(canFn, 'download', INVOICE_PERMISSIONS.SUBJECT)
    || hasPermission(canFn, 'manage', 'all')
}

/**
 * Whether the user may see one Invoice Tracker tab.
 * @param {Function} canFn
 * @param {string} subject — one of INVOICE_TAB_PERMISSIONS
 */
export function canViewInvoiceDashboardTab(canFn, subject) {
  if (!subject) return false
  if (isInvoiceDashboardAdmin(canFn)) return true
  return hasPermission(canFn, 'read', subject)
}

export function getAllowedInvoiceDashboardTabs(canFn) {
  return INVOICE_DASHBOARD_TABS.filter(tab => canViewInvoiceDashboardTab(canFn, tab.subject))
}

export function getAllowedInvoiceDashboardTabKeys(canFn) {
  return getAllowedInvoiceDashboardTabs(canFn).map(tab => tab.key)
}

/**
 * Pick a tab the user is allowed to open.
 * Query / deep-link first, then Invoice Overview (preferred default), then first allowed.
 * Returns null when the user has no tab permissions.
 */
export function resolveInvoiceDashboardTab(canFn, requested) {
  const allowed = getAllowedInvoiceDashboardTabKeys(canFn)
  if (!allowed.length) return null

  const req = String(requested || '').toLowerCase().trim()
  if (req) {
    const match = INVOICE_DASHBOARD_TABS.find(tab => (
      tab.key === req
      || tab.subject === req
      || tab.aliases.includes(req)
    ))
    if (match && allowed.includes(match.key)) return match.key
  }

  if (allowed.includes(DEFAULT_INVOICE_DASHBOARD_TAB)) {
    return DEFAULT_INVOICE_DASHBOARD_TAB
  }
  return allowed[0]
}

export default {
  INVOICE_PERMISSIONS,
  INVOICE_ACTIONS,
  INVOICE_TAB_PERMISSIONS,
  INVOICE_DASHBOARD_TAB_KEYS,
  DEFAULT_INVOICE_DASHBOARD_TAB,
  INVOICE_DASHBOARD_TABS,
  INVOICE_DOWNLOAD_DENIED_TOOLTIP,
  INVOICE_ABILITY_CATALOG,
  INVOICE_ROLE_ABILITIES_MAP,
  isInvoiceDashboardAdmin,
  canDownloadInvoices,
  canViewInvoiceDashboardTab,
  getAllowedInvoiceDashboardTabs,
  getAllowedInvoiceDashboardTabKeys,
  resolveInvoiceDashboardTab,
}
